import type {
    AnatomySampleMap,
    DocsTranslate
} from "../types";
import {
    NavigationMenu,
    PageContainer,
    SystemToolbar,
    EmbeddedMenu,
    UserShortcut,
    MainHeader,
    Breadcrumb,
    Pagination,
    Sidebar,
    Menubar,
    Stepper,
    Header,
    Button,
    Tabs,
    Text
} from "ncore-ui-kit";
import {
    SearchIcon,
    HomeIcon,
    FileIcon,
    UserIcon,
    StarIcon,
    BellIcon
} from "../icons";
import {
    shapeConfigs
} from "../sampleTokens";

const SAMPLE_NAVIGATION_STATE: NCoreUIKit.NavigationState = {
    routes: [
        {
            name: "home",
            key: "home"
        }
    ],
    index: 0
};

const SAMPLE_NAVIGATION: NCoreUIKit.Navigation = {
    getRootState: () => SAMPLE_NAVIGATION_STATE,
    getState: () => SAMPLE_NAVIGATION_STATE,
    addListener: () => () => undefined,
    navigate: () => undefined,
    goBack: () => undefined,
    canGoBack: () => true
};

const renderTabs = (t: DocsTranslate, value: string) => <Tabs
    onValueChange={() => undefined}
    value={value}
>
    <Tabs.List>
        <Tabs.Trigger
            title={t("sample.home")}
            icon={HomeIcon}
            value="first"
        />
        <Tabs.Trigger
            title={t("sample.library")}
            value="second"
            count={3}
        />
        <Tabs.Trigger
            title={t("sample.page")}
            value="third"
        />
    </Tabs.List>
</Tabs>;

export const navigationSamples: AnatomySampleMap = {
    Header: (t) => ({
        node: <Header
            actions={[
                {
                    accessibilityLabel: t("sample.search"),
                    onPress: () => undefined,
                    icon: SearchIcon
                },
                {
                    accessibilityLabel: t("sample.notification"),
                    onPress: () => undefined,
                    icon: BellIcon
                }
            ]}
            navigation={SAMPLE_NAVIGATION}
            isWorkWithHeaderSpace={false}
            isWrapSafeAreaContext={false}
            title={t("sample.title")}
            isGoBackEnabled={true}
        />,
        width: shapeConfigs.dialogMaxWidth
    }),
    Sidebar: (t) => ({
        node: <Sidebar.Provider
            compactBreakpoint={0}
            persistKey={null}
            shortcut={null}
        >
            <Sidebar>
                <Sidebar.Header>
                    <Sidebar.MenuButton
                        subTitle={t("sample.subtitle")}
                        title={t("sample.brand")}
                        icon={StarIcon}
                        size="large"
                    />
                </Sidebar.Header>
                <Sidebar.Content>
                    <Sidebar.Group
                        title={t("sample.label")}
                    >
                        <Sidebar.Menu>
                            <Sidebar.MenuItem>
                                <Sidebar.MenuButton
                                    title={t("sample.home")}
                                    icon={HomeIcon}
                                    isActive={true}
                                />
                                <Sidebar.MenuBadge
                                    count={3}
                                />
                            </Sidebar.MenuItem>
                            <Sidebar.MenuItem>
                                <Sidebar.MenuButton
                                    title={t("sample.library")}
                                    icon={FileIcon}
                                />
                            </Sidebar.MenuItem>
                            <Sidebar.MenuItem>
                                <Sidebar.MenuButton
                                    title={t("sample.item1")}
                                    icon={UserIcon}
                                />
                            </Sidebar.MenuItem>
                        </Sidebar.Menu>
                    </Sidebar.Group>
                </Sidebar.Content>
                <Sidebar.Footer>
                    <Sidebar.MenuButton
                        title={t("sample.name")}
                        icon={UserIcon}
                        size="large"
                    />
                </Sidebar.Footer>
            </Sidebar>
        </Sidebar.Provider>,
        maxScale: 2
    }),
    NavigationMenu: (t) => ({
        node: <NavigationMenu
            collapseBehaviour="never"
            title={t("sample.title")}
        >
            <NavigationMenu.Item
                title={t("sample.home")}
                value="first"
            >
                <NavigationMenu.Content>
                    <NavigationMenu.Group
                        title={t("sample.label")}
                    >
                        <NavigationMenu.Link
                            title={t("sample.item1")}
                            href="/"
                        />
                    </NavigationMenu.Group>
                </NavigationMenu.Content>
            </NavigationMenu.Item>
            <NavigationMenu.Item
                title={t("sample.library")}
                onPress={() => undefined}
                isActive={true}
            />
            <NavigationMenu.Item
                onPress={() => undefined}
                title={t("sample.page")}
            />
        </NavigationMenu>
    }),
    Breadcrumb: (t) => ({
        node: <Breadcrumb
            compactBehaviour="collapse"
        >
            <Breadcrumb.List>
                <Breadcrumb.Item>
                    <Breadcrumb.Link
                        onPress={() => undefined}
                        title={t("sample.home")}
                        icon={HomeIcon}
                    />
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Breadcrumb.Link
                        title={t("sample.library")}
                        onPress={() => undefined}
                    />
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Breadcrumb.Page
                        title={t("sample.page")}
                    />
                </Breadcrumb.Item>
            </Breadcrumb.List>
        </Breadcrumb>
    }),
    Menubar: (t) => ({
        node: <Menubar
            collapseBehaviour="never"
            variant="outline"
        >
            <Menubar.Menu
                menuKey="first"
            >
                <Menubar.Trigger
                    title={t("sample.home")}
                />
                <Menubar.Content>
                    <Menubar.Item
                        title={t("sample.action")}
                        onPress={() => undefined}
                    />
                </Menubar.Content>
            </Menubar.Menu>
            <Menubar.Menu
                menuKey="second"
            >
                <Menubar.Trigger
                    title={t("sample.library")}
                />
                <Menubar.Content>
                    <Menubar.Item
                        title={t("sample.action")}
                        onPress={() => undefined}
                    />
                </Menubar.Content>
            </Menubar.Menu>
            <Menubar.Menu
                menuKey="third"
            >
                <Menubar.Trigger
                    title={t("sample.page")}
                />
                <Menubar.Content>
                    <Menubar.Item
                        title={t("sample.action")}
                        onPress={() => undefined}
                    />
                </Menubar.Content>
            </Menubar.Menu>
        </Menubar>
    }),
    Tabs: (t) => ({
        states: [
            {
                label: t("sample.stateDefault"),
                node: renderTabs(t, "first")
            },
            {
                label: t("sample.stateActive"),
                node: renderTabs(t, "second")
            }
        ],
        width: shapeConfigs.drawerWidth
    }),
    EmbeddedMenu: (t) => ({
        node: <EmbeddedMenu
            buttons={[
                {
                    title: t("sample.home"),
                    redirectMain: "home",
                    icon: HomeIcon
                },
                {
                    title: t("sample.library"),
                    redirectMain: "library",
                    icon: FileIcon,
                    badge: 3
                },
                {
                    title: t("sample.item1"),
                    redirectMain: "profile",
                    icon: UserIcon
                }
            ]}
            siteLogoProps={{
                subTitle: t("sample.subtitle"),
                title: t("sample.brand")
            }}
            navigation={SAMPLE_NAVIGATION}
            isWorkWithSafeAreaView={false}
            isWorkWithHeaderSpace={false}
            id="anatomy-embedded-menu"
            isCollapse={true}
        />,
        maxScale: 2
    }),
    MainHeader: (t) => ({
        node: <MainHeader
            siteLogoProps={{
                subTitle: t("sample.subtitle"),
                title: t("sample.brand")
            }}
            renderRight={() => <Button
                title={t("sample.action")}
                onPress={() => undefined}
            />}
            isWorkWithSafeAreaView={false}
            isWorkWithSeparator={true}
        />,
        width: shapeConfigs.dialogMaxWidth
    }),
    UserShortcut: (t) => ({
        node: <UserShortcut
            avatarProps={{
                statusIndicatorType: "online",
                title: t("sample.name")
            }}
            subTitle={t("sample.subtitle")}
            isWorkWithCoreComplex={false}
            title={t("sample.name")}
        />
    }),
    SystemToolbar: (t) => ({
        node: <SystemToolbar
            actions={[
                {
                    onPress: () => undefined,
                    title: t("sample.item1"),
                    icon: UserIcon
                },
                {
                    title: t("sample.item2"),
                    onPress: () => undefined
                }
            ]}
            title={t("sample.title")}
            variant="outline"
        />
    }),
    Stepper: (t) => ({
        node: <Stepper
            steps={[
                {
                    title: t("sample.step1")
                },
                {
                    title: t("sample.step2")
                },
                {
                    title: t("sample.step3")
                }
            ]}
            compactBehaviour="never"
            activeIndex={1}
        />,
        width: shapeConfigs.dialogMaxWidth
    }),
    Pagination: () => ({
        node: <Pagination
            onPageChange={() => undefined}
            totalItems={100}
            pageSize={10}
            page={5}
        />,
        width: shapeConfigs.commandWidth
    }),
    PageContainer: (t) => ({
        node: <PageContainer
            isWorkWithHeaderSpace={false}
        >
            <Text
                variant="titleLargeSize"
                color="high"
            >
                {t("sample.title")}
            </Text>
            <Text
                variant="bodyMediumSize"
                color="mid"
            >
                {t("sample.body")}
            </Text>
        </PageContainer>,
        width: shapeConfigs.drawerWidth
    })
};
